// src/lib/availabilitySummary.ts
import { addDays } from "date-fns";
import { dayHasAvailability, type Interval } from "@/lib/availability";

export type AvailabilityChip = { text: string; tone?: "success" | "muted" | "default" };

function sod(d: Date) { const x = new Date(d); x.setHours(0,0,0,0); return x; }
function sameDay(a: Date, b: Date) {
  return a.getFullYear()===b.getFullYear() && a.getMonth()===b.getMonth() && a.getDate()===b.getDate();
}
function fmtDM(d: Date) { return `${String(d.getDate()).padStart(2,"0")}/${String(d.getMonth()+1).padStart(2,"0")}`; }

function datesNextNDays(windows: Interval[], n=30, from=new Date()) {
  const out: Date[] = [];
  const start = sod(from);
  for (let i=0;i<n;i++){
    const d = addDays(start, i);
    if (dayHasAvailability(d, windows)) out.push(d);
  }
  return out;
}

function groupConsecutive(dates: Date[]) {
  if (!dates.length) return [] as { from: Date; to: Date }[];
  const s = [...dates].sort((a,b)=>+a-+b);
  const groups: {from: Date; to: Date}[] = [];
  let from = s[0], prev = s[0];
  for (let i=1;i<s.length;i++){
    const cur = s[i], nextPrev = addDays(prev,1);
    if (sameDay(cur, nextPrev)) { prev = cur; continue; }
    groups.push({from, to: prev}); from = cur; prev = cur;
  }
  groups.push({from, to: prev});
  return groups;
}

function pattern(av: Date[]) {
  const set = new Set(av.map(d=>d.getDay())); // 0=Sun..6=Sat
  const hverdage = [1,2,3,4,5].every(v=>set.has(v)) && ![0,6].every(v=>set.has(v));
  const weekend  = [0,6].every(v=>set.has(v)) && ![1,2,3,4,5].every(v=>set.has(v));
  return { hverdage, weekend };
}

export function summarizeAvailability(windows: Interval[], daysAhead = 30) {
  const avDates = datesNextNDays(windows, daysAhead);
  const chips: AvailabilityChip[] = [];

  if (avDates.length === 0) {
    return { chips: [{ text: `Ingen ledige dage (${daysAhead}d)`, tone: "muted" }], shortHint: `Ingen ledig (${daysAhead}d)` };
  }

  const today = new Date();
  const todayHit = avDates.find(d => sameDay(d, today));
  if (todayHit) chips.push({ text: "I dag", tone: "success" });
  else {
    const tomorrow = addDays(today,1);
    if (avDates.find(d => sameDay(d, tomorrow))) chips.push({ text: "I morgen", tone: "success" });
  }

  const groups = groupConsecutive(avDates);
  if (groups[0]) {
    const g = groups[0];
    chips.push({ text: sameDay(g.from, g.to) ? `Næste: ${fmtDM(g.from)}` : `Næste: ${fmtDM(g.from)}–${fmtDM(g.to)}` });
  }

  const patt = pattern(avDates);
  if (patt.hverdage) chips.push({ text: "Hverdage" });
  if (patt.weekend)  chips.push({ text: "Weekend" });

  chips.push({ text: `${avDates.length} dage næste ${daysAhead}d`, tone: "muted" });

  const shortHint = chips[0]?.text ?? (groups[0] ? `Næste: ${fmtDM(groups[0].from)}` : "Ledig");
  return { chips, shortHint };
}
